import React, { useState, useEffect, useRef } from 'react';
import { 
    UserPlusIcon, 
    Bars3Icon,
    HomeIcon,
    CalendarIcon,
    Cog6ToothIcon,
    ArrowRightOnRectangleIcon,
    UserGroupIcon,
    BuildingStorefrontIcon,
    EnvelopeIcon
} from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserVenueTokens, getVenueUserProfile } from '../Services/UserService.ts';
import { getVenueFeatures } from '../Services/VenueService.ts';
import { isPublicRoute } from '../Services/SiteService.ts';
import Helper from '../helpers';

const FloatingTokenBadge = () => {
    const [tokens, setTokens] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [userName, setUserName] = useState('');
    const [venueFeatures, setVenueFeatures] = useState(null); // null = loading
    const hasFetched = useRef(false);
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const hasFeature = (f) => venueFeatures === null || venueFeatures.includes(f);
    
    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        let cancelled = false;

        const init = async () => {
            try {
                const jwt = await Helper.getCookie('wh-jwt');
                if (!jwt) { if (!cancelled) setIsVisible(false); return; }

                if (!cancelled) setIsVisible(true);

                // Admin status — from Helper (reads JWT claims)
                const admin = await Helper.userIsAdmin();
                if (!cancelled) setIsAdmin(admin);

                // Venue key — from Helper (reads JWT claims)
                const venueId = await Helper.getUserVenueId();
                if (!venueId) return;

                // All three fetches in parallel — using existing services
                const [tokenData, userProfile, features] = await Promise.allSettled([
                    getUserVenueTokens(venueId),
                    getVenueUserProfile(venueId),
                    getVenueFeatures(venueId),
                ]);

                if (cancelled) return;

                if (tokenData.status === 'fulfilled') setTokens(tokenData.value?.tokens || 0);
                if (userProfile.status === 'fulfilled') setUserName(userProfile.value?.name || '');
                if (features.status === 'fulfilled') setVenueFeatures(features.value);
                else setVenueFeatures([]);
            } catch {
                if (!cancelled) setIsVisible(false);
            }
        };

        init();
        return () => { cancelled = true; };
    }, []);

    // Hide on public routes
    useEffect(() => {
        if (isPublicRoute(location.pathname)) {
            setIsVisible(false);
        }
    }, [location.pathname]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNavClick = (path) => {
        setMenuOpen(false);
        navigate(path);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed top-4 right-4 z-50" ref={menuRef}>
            <div className="relative">
                <button
                    onClick={() => setMenuOpen(o => !o)}
                    className="backdrop-blur-sm bg-emerald-600/20 border border-emerald-500/30 rounded-full px-4 py-2 flex items-center space-x-3 shadow-lg hover:bg-emerald-600/30 transition-all duration-200 group"
                >
                    <UserPlusIcon className="h-5 w-5 text-emerald-400" />
                    <span className="text-emerald-400 font-semibold text-sm">
                        {tokens} {tokens === 1 ? 'token' : 'tokens'}
                    </span>
                    <div className="w-px h-4 bg-emerald-500/30"></div>
                    <Bars3Icon className="h-5 w-5 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
                </button>

                {menuOpen && (
                    <div className="absolute right-0 mt-2 w-64 backdrop-blur-sm bg-gray-900/95 border border-gray-700/50 rounded-xl shadow-xl animate-fade-in">
                        {/* User Info Header */}
                        <div className="px-4 py-3 border-b border-gray-700/50">
                            <p className="text-sm font-medium text-white">Welcome, {userName || 'User'}</p>
                            <p className="text-xs text-gray-400">{isAdmin ? 'Admin' : 'Member'}</p>
                        </div>

                        {/* Menu Items */}
                        <div className="py-2">
                            <button onClick={() => handleNavClick('/dashboard')}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors">
                                <HomeIcon className="h-4 w-4 mr-3" /> Dashboard
                            </button>
                            <button onClick={() => handleNavClick('/dashboard/sessions')}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors">
                                <CalendarIcon className="h-4 w-4 mr-3" /> Sessions
                            </button>
                            <button onClick={() => handleNavClick('/dashboard/classes')}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors">
                                <CalendarIcon className="h-4 w-4 mr-3" /> Classes
                            </button>
                            <button onClick={() => handleNavClick('/dashboard/sessions?selected=mysessions')}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors">
                                <CalendarIcon className="h-4 w-4 mr-3" /> My Sessions
                            </button>

                            {isAdmin && (
                                <>
                                    <div className="my-1 border-t border-gray-700/50"></div>
                                    {hasFeature('sessions') && (
                                    <button onClick={() => handleNavClick('/dashboard/adminSession')}
                                        className="w-full flex items-center px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 transition-colors">
                                        <UserGroupIcon className="h-4 w-4 mr-3" /> Admin Sessions
                                    </button>
                                    )}
                                    {hasFeature('classes') && (
                                        <button onClick={() => handleNavClick('/dashboard/adminClasses')}
                                            className="w-full flex items-center px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 transition-colors">
                                            <UserGroupIcon className="h-4 w-4 mr-3" /> Admin Classes
                                        </button>
                                    )}
                                    {hasFeature('bulk_manage') && (
                                        <>
                                            <button onClick={() => handleNavClick('/dashboard/bulk-session-management')}
                                                className="w-full flex items-center px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 transition-colors">
                                                <UserGroupIcon className="h-4 w-4 mr-3" /> Bulk Sessions Mgt
                                            </button>
                                            <button onClick={() => handleNavClick('/dashboard/bulk-session')}
                                                className="w-full flex items-center px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 transition-colors">
                                                <Cog6ToothIcon className="h-4 w-4 mr-3" /> Bulk Sessions
                                            </button>
                                            <button onClick={() => handleNavClick('/dashboard/addmanyclasses')}
                                                className="w-full flex items-center px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 transition-colors">
                                                <UserGroupIcon className="h-4 w-4 mr-3" /> Bulk Classes
                                            </button>
                                        </>
                                    )}
                                    {hasFeature('financial_reporting') && (
                                        <button onClick={() => handleNavClick('/dashboard/reports')}
                                            className="w-full flex items-center px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 transition-colors">
                                            <UserGroupIcon className="h-4 w-4 mr-3" /> Reports
                                        </button>
                                    )}
                                    <div className="my-1 border-t border-gray-700/50"></div>
                                    <button onClick={() => handleNavClick('/dashboard/venue-listing')}
                                        className="w-full flex items-center px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 transition-colors">
                                        <BuildingStorefrontIcon className="h-4 w-4 mr-3" /> PT Directory Settings
                                    </button>
                                    <button onClick={() => handleNavClick('/dashboard/pt-enquiries')}
                                        className="w-full flex items-center px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 transition-colors">
                                        <EnvelopeIcon className="h-4 w-4 mr-3" /> PT Enquiries
                                    </button>
                                </>
                            )}

                            <div className="my-1 border-t border-gray-700/50"></div>
                            <button onClick={() => handleNavClick('/dashboard/profile')}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors">
                                <UserPlusIcon className="h-4 w-4 mr-3" /> Profile
                            </button>
                            <button onClick={() => navigate('/logout')}
                                className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-colors">
                                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" /> Logout
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FloatingTokenBadge;
